import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { CloudRain } from "lucide-react";
import { MapBase } from "../MapBase";
import { MapStore } from "@/components/Map/stores/MapStore";
import { MapLoadingProps } from "@/types/map";
import { useUrlQuerySync } from "@/hooks/useUrlQuerySync";
import { wireMarkerPopups } from "@/components/Map/stores/wireMarkerPopups";
import type { RightClick, RightClickMenuAction } from "@/components/Map/RightClickMenu/RightClickMenuStore";
import type { MapRightClickTarget } from "@/components/Map/stores/mapRightClickTarget";
import type { TimePeriodsValue } from "@/config/date";
import { MapFilters } from "../ListMapView";
import { MapPopups } from "./MapPopups";
import { MapControls } from "./MapControls";
import { RainControls, RainGauge, RainPopup } from "./Rain";
import { RightClickMenu } from "./RightClickMenu";
import { OverlayHoverPopup } from "./Overlays/OverlayHoverPopup";
import {
	ConnectionsControls,
	ConnectionsBanner,
	ConnectionsCreatingMarker,
	ConnectionsHoverPopup,
} from "./Connections";

const DEFAULT_RAIN_TIME_PERIOD: TimePeriodsValue = "last1y";

export interface MapProps<TData> {
	mapStore: MapStore<TData>;
	renderPopup?: (item: TData, filters: MapFilters) => React.ReactNode;
	loader?: MapLoadingProps;
	initFromUrl?: boolean;
	enableUrlParams?: boolean;
	/** Slot for components that need to live inside react-map-gl's <Map> context. */
	children?: React.ReactNode;
	/** When provided, builds the right-click action list for a given click. */
	rightClickActions?: (click: RightClick<MapRightClickTarget>) => RightClickMenuAction<MapRightClickTarget>[];
	/** Time period passed to the rain popups (gauge + radar). */
	rainTimePeriod?: TimePeriodsValue;
}

export const Map = observer(
	<TData,>({
		mapStore,
		renderPopup,
		loader = {},
		initFromUrl = false,
		enableUrlParams = false,
		children,
		rightClickActions,
		rainTimePeriod,
	}: MapProps<TData>) => {
		useUrlQuerySync(
			{ filters: mapStore.filters.snapshot },
			{ setFilters: mapStore.filters.setFilters },
			{ enableUrlParams, initFromUrl },
		);

		useEffect(() => {
			if (mapStore.onFetch) mapStore.onFetch();
		}, []);

		useEffect(() => {
			if (!mapStore.isLoaded) return;
			const map = mapStore.mapRef.current?.getMap();
			if (!map) return;
			mapStore.rightClickMenuStore.attach(map);
			const disposePopups = wireMarkerPopups(mapStore, mapStore.popupsStore);
			return () => {
				mapStore.rightClickMenuStore.detach();
				disposePopups();
			};
		}, [mapStore.isLoaded, mapStore]);

		return (
			<div className="flex-1 min-h-0 flex relative">
				<MapBase
					mapRef={mapStore.mapRef}
					onLoad={() => {
						const map = mapStore.mapRef.current;
						if (map) mapStore.onLoad(map);
					}}
					loader={loader}
				>
					{renderPopup && (
						<MapPopups
							popupsStore={mapStore.popupsStore}
							renderPopup={id => {
								const item = mapStore.getMarkerById(id) as TData | undefined;
								if (!item) return null;
								return renderPopup(item, {
									getFilter: filterId => mapStore.filters.getFilter(filterId),
									setFilter: (filterId, value) => mapStore.filters.setFilter(filterId, value),
									clearFilter: filterId => mapStore.filters.clear(filterId),
									filters: mapStore.filters.snapshot,
								});
							}}
						/>
					)}
					<OverlayHoverPopup overlaysStore={mapStore.overlaysStore} />
					<RightClickMenu
						rightClickMenuStore={mapStore.rightClickMenuStore}
						actions={click => [
							{
								id: "rain.showData",
								label: "Show rain data",
								icon: <CloudRain className="h-4 w-4 text-cyan-500" />,
								onSelect: () =>
									mapStore.popupsStore.openClickPopup({
										kind: "rain",
										lng: click.lng,
										lat: click.lat,
									}),
							},
							...(rightClickActions?.(click) ?? []),
						]}
					/>
					{mapStore.popupsStore.clickPopup?.kind === "rain" && (
						<RainPopup
							lng={mapStore.popupsStore.clickPopup.lng}
							lat={mapStore.popupsStore.clickPopup.lat}
							timePeriod={rainTimePeriod ?? DEFAULT_RAIN_TIME_PERIOD}
							onClose={() => {
								mapStore.rainStore.rainLayers.hideGaugeConnection();
								mapStore.popupsStore.closeClickPopup();
							}}
							onSourceInfoChange={sourceInfo => {
								const popup = mapStore.popupsStore.clickPopup;
								if (popup?.kind !== "rain") return;
								if (!sourceInfo?.station || sourceInfo.source !== "rain_gauge") {
									mapStore.rainStore.rainLayers.hideGaugeConnection();
									return;
								}
								mapStore.rainStore.rainLayers.showGaugeConnection(
									popup.lng,
									popup.lat,
									sourceInfo.station.lon,
									sourceInfo.station.lat,
									sourceInfo.station.distanceKm,
								);
							}}
						/>
					)}
					<RainGauge mapStore={mapStore} timePeriod={rainTimePeriod} />
					<ConnectionsCreatingMarker mapStore={mapStore} />
					<ConnectionsHoverPopup mapStore={mapStore} />
					{children}
				</MapBase>
				<MapControls mapStore={mapStore} />
				<RainControls mapStore={mapStore} />
				<ConnectionsControls mapStore={mapStore} />
				<ConnectionsBanner mapStore={mapStore} />
			</div>
		);
	},
);
